'use client';

import React from 'react';
import { toast } from 'sonner';
import { ALL_MATCHES, ALL_PROPERTIES } from '@/lib/data/sharedData';

// Build top matches from shared data
const topMatches = ALL_MATCHES
  .sort((a, b) => b.scoreGlobal - a.scoreGlobal)
  .slice(0, 5)
  .map((m, i) => {
    const prop = ALL_PROPERTIES.find(p => p.ref === m.localRef);
    const statusMap: Record<string, { label: string; color: string }> = {
      'En discussion': { label: 'En discussion', color: 'purple' },
      'En négociation': { label: 'En négociation', color: 'amber' },
      'Contacté': { label: 'Proposition envoyée', color: 'blue' },
      'Planifié': { label: 'Visite planifiée', color: 'amber' },
      'À contacter': { label: 'Nouveau match', color: 'green' },
    };
    const statusInfo = statusMap[m.statut] || { label: m.statut, color: 'green' };
    return {
      id: i + 1,
      property: m.localTitre,
      city: m.localVille,
      surface: `${m.localSurface} m²`,
      loyer: `${m.localLoyer.toLocaleString('fr-MA')} MAD`,
      tenant: m.enseigne,
      sector: m.secteur,
      score: m.scoreGlobal,
      status: statusInfo.label,
      statusColor: statusInfo.color,
      broker: prop?.broker || '—',
    };
  });

const statusStyles: Record<string, { bg: string; text: string }> = {
  blue: { bg: 'bg-blue-50', text: 'text-blue-700' },
  amber: { bg: 'bg-amber-50', text: 'text-amber-700' },
  purple: { bg: 'bg-purple-50', text: 'text-purple-700' },
  green: { bg: 'bg-green-50', text: 'text-green-700' },
  red: { bg: 'bg-red-50', text: 'text-red-700' },
};

function ScoreBadge({ score }: { score: number }) {
  const color = score >= 90 ? 'text-green-700 bg-green-50 border-green-200'
    : score >= 80 ? 'text-blue-700 bg-blue-50 border-blue-200' : 'text-amber-700 bg-amber-50 border-amber-200';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-700 border tabular-nums ${color}`}>
      {score}%
    </span>
  );
}

export default function TopMatchesTable() {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-card overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div>
          <h3 className="text-base font-600 text-slate-900">Top matchs du jour</h3>
          <p className="text-xs text-slate-400 mt-0.5">Meilleurs scores de compatibilité local ↔ enseigne</p>
        </div>
        <a
          href="/matching"
          className="text-xs font-600 text-primary-700 hover:text-primary-600 hover:underline"
        >
          Voir tous les matchs →
        </a>
      </div>

      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50/60">
              <th className="table-th">Local</th>
              <th className="table-th">Enseigne</th>
              <th className="table-th">Surface</th>
              <th className="table-th">Loyer</th>
              <th className="table-th">Score</th>
              <th className="table-th">Statut</th>
              <th className="table-th">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {topMatches.map(match => {
              const s = statusStyles[match.statusColor] || statusStyles.green;
              return (
                <tr key={match.id} className="hover:bg-slate-50/60 transition-colors group">
                  <td className="table-td">
                    <div>
                      <p className="font-600 text-slate-800 text-sm leading-tight">{match.property}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{match.city} · {match.broker}</p>
                    </div>
                  </td>
                  <td className="table-td">
                    <div>
                      <p className="font-600 text-slate-800 text-sm">{match.tenant}</p>
                      <p className="text-xs text-slate-400">{match.sector}</p>
                    </div>
                  </td>
                  <td className="table-td">
                    <span className="font-mono text-sm tabular-nums text-slate-700">{match.surface}</span>
                  </td>
                  <td className="table-td">
                    <span className="font-mono text-sm tabular-nums text-slate-700">{match.loyer}</span>
                  </td>
                  <td className="table-td">
                    <ScoreBadge score={match.score} />
                  </td>
                  <td className="table-td">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-500 ${s.bg} ${s.text}`}>
                      {match.status}
                    </span>
                  </td>
                  <td className="table-td">
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => toast.success(`Fiche "${match.property}" ouverte`)}
                        title="Voir la fiche"
                        className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => toast.info(`Visite planifiée pour "${match.property}"`)}
                        title="Planifier une visite"
                        className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}